import { LocalAudioTrack } from 'twilio-video';
import { useCallback, useRef } from 'react';
import useIsTrackEnabled from '../useIsTrackEnabled/useIsTrackEnabled';
import useVideoContext from '../useVideoContext/useVideoContext';

export default function useLocalAudioToggle() {
	const {
		localTracks,
		room,
		getLocalAudioTrack,
		restartAudioTrack,
		discardLocalTrack,
		onError,
	} = useVideoContext();
	const audioTrack = localTracks.find(
		(track) => track.kind === 'audio',
	) as LocalAudioTrack;
	const isEnabled = useIsTrackEnabled(audioTrack);

	const pending = useRef(false);
	const toggleAudioEnabled = useCallback(() => {
		if (pending.current) return;
		if (
			audioTrack &&
			!audioTrack.isStopped &&
			(
				audioTrack.noiseCancellation?.sourceTrack ||
				audioTrack.mediaStreamTrack
			)?.readyState !== 'ended'
		) {
			audioTrack.isEnabled ? audioTrack.disable() : audioTrack.enable();
			return;
		}
		pending.current = true;
		let acquired = audioTrack;
		void (async () => {
			if (audioTrack) {
				await restartAudioTrack({});
				audioTrack.enable();
			} else {
				const track = await getLocalAudioTrack();
				acquired = track;
				if (track.isStopped || room?.state === 'disconnected') {
					track.stop();
					return;
				}
				track.enable();
				await room?.localParticipant.publishTrack(track);
			}
		})()
			.catch((error) => {
				if (acquired) discardLocalTrack(acquired);
				if (
					room?.state === 'disconnected' ||
					/cancelled/i.test(error.message)
				)
					return;
				onError(
					new Error(
						'Unable to use your microphone. Check microphone permission and try again.',
					),
				);
			})
			.finally(() => {
				pending.current = false;
			});
	}, [
		audioTrack,
		room,
		getLocalAudioTrack,
		restartAudioTrack,
		discardLocalTrack,
		onError,
	]);

	return [isEnabled, toggleAudioEnabled] as const;
}
